import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LuChevronDown } from "react-icons/lu";
import { RiAspectRatioLine } from "@remixicon/react";
import { cn } from "@/lib/utils";

export type AspectRatio = "Auto" | "9:16" | "2:3" | "4:5" | "1:1" | "3:2" | "16:9";

interface Props {
  value: AspectRatio;
  onChange: (value: AspectRatio) => void;
  allowedOptions?: AspectRatio[];
}

const OPTIONS: AspectRatio[] = ["Auto", "9:16", "2:3", "4:5", "1:1", "3:2", "16:9"];

const AspectRatioDropdown: React.FC<Props> = ({ value, onChange, allowedOptions }) => {
  const options = allowedOptions ?? OPTIONS;
  const renderRatioIcon = (ratio: AspectRatio) => {
    if (ratio === "Auto") {
      return;
    }
    const [rwStr, rhStr] = ratio.split(":");
    const rw = Math.max(parseInt(rwStr || "1", 10) || 1, 1);
    const rh = Math.max(parseInt(rhStr || "1", 10) || 1, 1);
    const base = 12;
    const isWide = rw >= rh;
    const boxW = isWide ? base : Math.round(base * (rw / rh));
    const boxH = isWide ? Math.round(base * (rh / rw)) : base;
    return (
      <span className="inline-flex items-center justify-center w-[14px] h-[14px] rounded-sm border border-gray-300">
        <span
          className="block rounded-[2px] bg-gray-200"
          style={{ width: `${boxW}px`, height: `${boxH}px` }}
        />
      </span>
    );
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-[6px] border-[1px]">
          <RiAspectRatioLine size={14} className="text-brand-primary-400 shrink-0" />
          <span className="text-brand-primary-800 text-[12px]">{value}</span>
          <LuChevronDown className="h-4 w-4 text-brand-primary-new shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="p-1 bg-white shadow-none rounded-[12px] border-0 squircle squircle-radius-[12px] squircle-smooth-[1] squircle-outline-[1px] squircle-outline-color-border-gray"
      >
        {options.map((opt) => (
          <DropdownMenuItem
            key={opt}
            className={cn(
              "flex gap-2 h-[28px] px-2 py-1 items-center self-stretch cursor-pointer text-brand-primary-800 hover:text-brand-primary-800 text-[12px]",
              opt === value ? "bg-selected-gray" : "hover:bg-hover-gray",
            )}
            onClick={() => onChange(opt)}
          >
            {renderRatioIcon(opt)}
            <span>{opt}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AspectRatioDropdown;
